import React, {useEffect, useState} from "react";
import Loader from "../../components/Loader";
import axiosInstance from "../../utils/axiosInstance";
import {ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon, BoltIcon,} from "@heroicons/react/24/outline";
import {IoArrowBackSharp} from "react-icons/io5";

const CreditsLedger = () => {
    const [response, setResponse] = useState();
    const [credits, setCredits] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 35;

    useEffect(() => {
        const fetchCredits = async () => {
            setLoading(true);
            try {
                const offset = (currentPage - 1) * itemsPerPage;
                const response = await axiosInstance.get(
                    `/credits-ledger?limit=${itemsPerPage}&offset=${offset}`
                );
                setCredits(response.data.credits_ledger);

                // Calculate the total number of pages
                const totalRows = response.data.total_rows;
                setTotalPages(Math.ceil(totalRows / itemsPerPage));
                setResponse(response.data);
            } catch (error) {
                console.error("Error fetching data", error);
            }
            setLoading(false);
        };
        fetchCredits();
    }, [currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="container mx-auto p-6">
                <Loader/>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center">
                {/* Go Back Button */}
                <button
                    onClick={() => window.history.back()} // This will navigate back to the previous page
                    className="rounded-full hover:bg-gray-200 transition-all mr-2"
                >
                    <IoArrowBackSharp size={24}/>
                </button>
                {" "}
                Credits Ledger
            </h3>
            <div className="flex flex-wrap -mx-6 mb-8">
                <div className="w-full px-6 sm:w-1/3 xl:w-1/5">
                    <div className="flex items-center px-5 py-6 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                        <div className="p-3 bg-green-600 text-white bg-opacity-75 rounded-full">
                            <ArrowsUpDownIcon className="w-5 h-5"/>
                        </div>

                        <div className="mx-5">
                            <h4 className="text-2xl font-semibold text-gray-700 dark:text-gray-100">
                                {loading ? "..." : response?.admin}
                            </h4>
                            <div className="text-gray-500 dark:text-gray-400">from Admin</div>
                        </div>
                    </div>
                </div>

                <div className="w-full px-6 sm:w-1/3 xl:w-1/5 sm:mt-0">
                    <div className="flex items-center px-5 py-6 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                        <div className="p-3 bg-pink-600 text-white bg-opacity-75 rounded-full">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                className="size-6"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
                                />
                            </svg>
                        </div>

                        <div className="mx-5">
                            <h4 className="text-2xl font-semibold text-gray-700 dark:text-gray-100">
                                {loading ? "..." : response?.refund}
                            </h4>
                            <div className="text-gray-500 dark:text-gray-400">
                                from Refund
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full px-6 sm:w-1/3 xl:w-1/5 xl:mt-0">
                    <div className="flex items-center px-5 py-6 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                        <div className="p-3 bg-green-600 text-white bg-opacity-75 rounded-full">
                            <ArrowDownIcon className="w-5 h-5"/>
                        </div>

                        <div className="mx-5">
                            <h4 className="text-2xl font-semibold text-gray-700 dark:text-gray-100">
                                {loading ? "..." : response?.paypal}
                            </h4>
                            <div className="text-gray-500 dark:text-gray-400">
                                from PayPal
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full px-6 sm:w-1/3 xl:w-1/5 xl:mt-0">
                    <div className="flex items-center px-5 py-6 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                        <div className="p-3 bg-red-600 text-white bg-opacity-75 rounded-full">
                            <ArrowUpIcon className="w-5 h-5"/>
                        </div>

                        <div className="mx-5">
                            <h4 className="text-2xl font-semibold text-gray-700 dark:text-gray-100">
                                {loading ? "..." : response?.upload}
                            </h4>
                            <div className="text-gray-500 dark:text-gray-400">
                                from Upload
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full px-6 sm:w-1/3 xl:w-1/5 xl:mt-0">
                    <div className="flex items-center px-5 py-6 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                        <div className="p-3 bg-red-600 text-white bg-opacity-75 rounded-full">
                            <BoltIcon className="w-5 h-5"/>
                        </div>

                        <div className="mx-5">
                            <h4 className="text-2xl font-semibold text-gray-700 dark:text-gray-100">
                                {loading ? "..." : response?.automatic}
                            </h4>
                            <div className="text-gray-500 dark:text-gray-400">
                                from Automatic
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
                        <thead>
                        <tr className="w-full bg-gray-100 text-left">
                            <th className="px-4 py-2">UserName</th>
                            <th className="px-4 py-2">Credit Balance</th>
                            <th className="px-4 py-2">Source</th>
                            <th className="px-4 py-2">Created At</th>
                        </tr>
                        </thead>
                        <tbody>
                        {credits.map((credit) => (
                            <tr key={credit.id} className="border-b">
                                <td className="px-4 py-2">{credit.username}</td>
                                <td
                                    className={`px-4 py-2 font-semibold ${
                                        credit.credit_balance >= 0
                                            ? "text-green-500"
                                            : "text-red-500"
                                    }`}
                                >
                                    {credit.credit_balance}
                                </td>
                                <td className="px-4 py-2">{credit.source}</td>
                                <td className="px-4 py-2">
                                    {new Date(credit.created_at).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            }

            <div className="flex justify-center items-center space-x-2 mt-4">
                {Array.from({length: totalPages}, (_, index) => (
                    <button
                        key={index}
                        onClick={() => handlePageChange(index + 1)}
                        className={`px-3 py-1 rounded ${
                            currentPage === index + 1
                                ? "bg-blue-500 text-white"
                                : "bg-gray-200"
                        }`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default CreditsLedger;